import type { LiveBall } from "./useLiveStats";

/** Runs the batter did not score off the bat: never drawn on a shot map. */
const NOT_OFF_THE_BAT = new Set(["WIDE", "BYE", "LEG_BYE", "NO_BALL_BYE", "NO_BALL_LEG_BYE"]);

/** The scorer sends the enum name, or NONE when no type was chosen. Returns a readable label, or null when unrecorded. */
export const shotLabel = (t?: string | null) => {
  const s = (t ?? "").trim();
  if (!s || /^none$/i.test(s)) return null;
  const x = s.replace(/_/g, " ").toLowerCase();
  return x.charAt(0).toUpperCase() + x.slice(1);
};

export const isLeftHander = (b: Pick<LiveBall, "batsmanBattingStyle">) => /LEFT/i.test(b.batsmanBattingStyle ?? "");

/** A ball the batter scored off with a recorded direction, i.e. one that can be drawn. */
export const isShotBall = (b: LiveBall) => b.runs > 0 && !!b.sector && !(b.extraType && NOT_OFF_THE_BAT.has(b.extraType));

/**
 * Centre angle of each field sector for a right-hander, in degrees: 0 is straight down the ground, positive is the
 * off side, negative the leg side. Sector names are relative to the batter, so a left-hander is drawn mirrored.
 */
export const SECTORS: Record<string, { label: string; from: number; to: number }> = {
  LONG_OFF: { label: "Long off", from: 0, to: 45 },
  COVER: { label: "Cover", from: 45, to: 90 },
  POINT: { label: "Point", from: 90, to: 135 },
  THIRD_MAN: { label: "Third man", from: 135, to: 180 },
  FINE_LEG: { label: "Fine leg", from: -180, to: -135 },
  SQUARE_LEG: { label: "Square leg", from: -135, to: -90 },
  MID_WICKET: { label: "Mid wicket", from: -90, to: -45 },
  LONG_ON: { label: "Long on", from: -45, to: 0 },
};
export const ZONE_ORDER = ["LONG_OFF", "COVER", "POINT", "THIRD_MAN", "FINE_LEG", "SQUARE_LEG", "MID_WICKET", "LONG_ON"];

export const RUN_KEYS = ["1", "2", "3", "4", "6"] as const;
export type RunKey = (typeof RUN_KEYS)[number] | "all";
export const runKey = (r: number): RunKey => (r >= 6 ? "6" : r >= 4 ? "4" : r === 3 ? "3" : r === 2 ? "2" : "1");
export const RUN_COLOR: Record<string, string> = { "1": "#FFFFFF", "2": "#F7D774", "3": "#F08A3C", "4": "#1D4ED8", "6": "#B0287A" };
export const RUN_LABEL: [RunKey, string][] = [["all", "All"], ["1", "1s"], ["2", "2s"], ["3", "3s"], ["4", "4s"], ["6", "6s"]];

/** Stable 0..1 value per ball so spikes spread inside their sector and stay put across polls. */
export const unit = (id: number, salt: number) => {
  const x = Math.sin(id * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
};
